import React, { useEffect, useState } from 'react';
import Modal from '../../BaseComponents/Modal';
import DexaResultsDashboard from './DexaResultsDashboard';
import './ExtractedDataModal.scss';

interface ExtractedDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  extractedData: Record<string, unknown> | null;
  mode: 'view' | 'edit';
  onSave: (data: Record<string, unknown>) => Promise<void>;
}

const ExtractedDataModal: React.FC<ExtractedDataModalProps> = ({
  isOpen,
  onClose,
  extractedData,
  mode,
  onSave
}) => {
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setJsonText(JSON.stringify(extractedData || {}, null, 2));
      setError(null);
    }
  }, [isOpen, extractedData, mode]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const parsed = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('Extracted data must be a JSON object');
        return;
      }
      await onSave(parsed);
    } catch (saveError: any) {
      setError(saveError?.message || 'Invalid JSON. Please fix and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const isViewMode = mode === 'view';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isViewMode ? 'View Extracted Data' : 'Update Extracted Data'}
      width='720px'
    >
      <div className={`extracted-data-modal ${isViewMode ? 'view-mode' : 'edit-mode'}`}>
        {isViewMode ? (
          <>
            <DexaResultsDashboard data={extractedData} />
            <div className='actions'>
              <button type='button' className='cancel-btn' onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <p className='edit-hint'>Edit the extracted JSON below, then save to update the report.</p>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={20}
              disabled={isSaving}
            />
            {error && <div className='error-message'>{error}</div>}
            <div className='actions'>
              <button type='button' className='cancel-btn' onClick={onClose} disabled={isSaving}>
                Cancel
              </button>
              <button type='button' className='save-btn' onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ExtractedDataModal;
